import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Trash2, MapPin } from "lucide-react";

export default function HotelsLog({ trip, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", address: "", check_in: "", check_out: "", confirmation: "", notes: "" });
  const save = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try { const { data } = await api.post(`/trips/${trip.trip_id}/hotels`, f); onUpdate(data); setOpen(false); setF({ name: "", address: "", check_in: "", check_out: "", confirmation: "", notes: "" }); toast.success("Stay added"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { const { data } = await api.delete(`/trips/${trip.trip_id}/hotels/${id}`); onUpdate(data); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl font-semibold">Stays</h2>
        <Button size="sm" onClick={()=>setOpen(true)} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-hotel-button"><Plus size={14} className="mr-1"/> Add stay</Button>
      </div>
      {trip.hotels.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-stone-300 rounded-2xl bg-white">
          <Building2 size={26} className="mx-auto text-stone-400 mb-2"/>
          <div className="text-stone-500">No stays logged yet.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {trip.hotels.map(h => (
            <div key={h.id} className="rounded-2xl border border-stone-200 bg-white p-5 group" data-testid={`hotel-${h.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-emerald-700">Stay</div>
                  <div className="font-serif-display text-xl font-semibold mt-1">{h.name}</div>
                  {h.address && <div className="text-xs text-stone-500 flex items-center gap-1 mt-1"><MapPin size={11}/>{h.address}</div>}
                </div>
                <button onClick={()=>del(h.id)} data-testid={`hotel-delete-${h.id}`} className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 size={16}/></button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><div className="font-mono-code text-[10px] uppercase text-stone-500">Check-in</div><div>{h.check_in || "—"}</div></div>
                <div><div className="font-mono-code text-[10px] uppercase text-stone-500">Check-out</div><div>{h.check_out || "—"}</div></div>
              </div>
              {h.confirmation && <div className="mt-3 font-mono-code text-xs text-stone-600">Conf: {h.confirmation}</div>}
              {h.notes && <div className="text-sm text-stone-600 mt-2">{h.notes}</div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Log stay</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} className="mt-1" data-testid="hotel-name-input"/></div>
            <div><Label>Address</Label><Input value={f.address} onChange={(e)=>setF({...f, address: e.target.value})} className="mt-1"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Check-in</Label><Input type="date" value={f.check_in} onChange={(e)=>setF({...f, check_in: e.target.value})} className="mt-1"/></div>
              <div><Label>Check-out</Label><Input type="date" value={f.check_out} onChange={(e)=>setF({...f, check_out: e.target.value})} className="mt-1"/></div>
            </div>
            <div><Label>Confirmation</Label><Input value={f.confirmation} onChange={(e)=>setF({...f, confirmation: e.target.value})} className="mt-1"/></div>
            <div><Label>Notes</Label><Textarea value={f.notes} onChange={(e)=>setF({...f, notes: e.target.value})} className="mt-1"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="hotel-save-button">Save stay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
